/**
 * 動画ファイルからフレームを逐次取り出すユーティリティ。
 * 全フレームを配列に溜めるとメモリを圧迫するため、コールバック（onFrame）で
 * 1フレームずつ処理してから次のフレームへ進むストリーミング方式にしている。
 */

/** サンプリングの上限 fps（動画が長くてもこれ以上は間引く）。 */
const SAMPLING_FPS = 15;

/** シーク完了（seeked イベント）を待つタイムアウト（ms）。これを超えたらそのフレームを諦めて次へ進む。 */
const SEEK_TIMEOUT_MS = 5000;

/** フレームが描画可能（readyState が HAVE_CURRENT_DATA 以上）になるまで待つタイムアウト（ms）。 */
const READY_TIMEOUT_MS = 10000;

export interface VideoFrameInfo {
  canvas: OffscreenCanvas | HTMLCanvasElement;
  width: number;
  height: number;
  /** 動画内の時刻（ms）。 */
  timestampMs: number;
  /** そのフレームで実際に使ったクロップ範囲（動画正規化座標）。全体描画なら null。 */
  cropRect: CropRect | null;
}

/** 動画正規化座標（0〜1）でのクロップ範囲。 */
export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * forEachVideoFrame の呼び出し側が可変のクロップ範囲を渡すためのコントローラ。
 * 毎フレーム描画直前に rect を読み取るので、呼び出し側は onFrame 内で次フレーム用の
 * rect を書き換えれば、前フレームの検出結果に応じたクロップ追従ができる。
 * rect が null なら動画全体を描画する。
 */
export interface CropController {
  rect: CropRect | null;
}

/** loadedmetadata イベントを Promise 化して待つ。 */
function waitForLoadedMetadata(video: HTMLVideoElement): Promise<void> {
  return new Promise((resolve, reject) => {
    const onLoaded = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      // iPhone の HEVC など、ブラウザが再生できないコーデックで発生しうる。
      reject(new Error('この動画を読み込めませんでした。MP4(H.264) など一般的な形式でお試しください。'));
    };
    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('error', onError);
    };
    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('error', onError);
  });
}

/**
 * loadedmetadata の時点ではまだピクセルデータが届いておらず drawImage しても空になるため、
 * 実際にフレームを描画できる状態（readyState が HAVE_CURRENT_DATA 以上）になるまで待つ。
 * preload = 'auto' なので通常はすぐ揃うが、念のためタイムアウトを設けて分かりやすいエラーに変える。
 */
function waitUntilFrameReady(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const onLoadedData = () => {
      cleanup();
      resolve();
    };
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('この動画のフレームを読み込めませんでした。別の動画でお試しください。'));
    }, READY_TIMEOUT_MS);
    const cleanup = () => {
      video.removeEventListener('loadeddata', onLoadedData);
      clearTimeout(timer);
    };
    video.addEventListener('loadeddata', onLoadedData);
  });
}

/**
 * 一部の webm 録画で loadedmetadata 直後の duration が Infinity/NaN になる既知の問題への
 * 簡易ワークアラウンド。末尾へ大きくシークして durationchange を待ち、正しい duration を
 * 取得し直す。取得できなければ諦めて分かりやすいエラーを投げる（過剰な対応はしない）。
 */
async function resolveDuration(video: HTMLVideoElement): Promise<number> {
  if (Number.isFinite(video.duration) && video.duration > 0) return video.duration;

  await new Promise<void>((resolve) => {
    const onDurationChange = () => {
      video.removeEventListener('durationchange', onDurationChange);
      resolve();
    };
    video.addEventListener('durationchange', onDurationChange);
    video.currentTime = Number.MAX_SAFE_INTEGER;
    // durationchange が発火しない環境向けに一定時間で諦める。
    setTimeout(resolve, 1000);
  });
  video.currentTime = 0;

  if (!Number.isFinite(video.duration) || video.duration <= 0) {
    throw new Error('この動画の長さを取得できませんでした。別の動画でお試しください。');
  }
  return video.duration;
}

/** 指定時刻へシークし、seeked イベント（またはタイムアウト）を待つ。 */
function seekTo(video: HTMLVideoElement, timeSec: number): Promise<boolean> {
  // すでに目的の時刻にいて、かつ現在のフレームが描画可能な場合、currentTime へ同じ値を
  // 代入してもブラウザ（特に Chrome）が no-op とみなして seeked を発火しないことがある。
  // これに気付かず待ち続けると SEEK_TIMEOUT_MS を無駄に浪費してフレームを取りこぼすため、
  // その場合は現在のフレームをそのまま使ってよいと判断して即座に true を返す。
  if (Math.abs(video.currentTime - timeSec) < 1e-3 && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    let settled = false;
    const onSeeked = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(true);
    };
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      // タイムアウトしたフレームはスキップして続行する（1フレームの失敗で全体を止めない）。
      resolve(false);
    }, SEEK_TIMEOUT_MS);
    const cleanup = () => {
      video.removeEventListener('seeked', onSeeked);
      clearTimeout(timer);
    };
    video.addEventListener('seeked', onSeeked);
    video.currentTime = timeSec;
  });
}

export async function forEachVideoFrame(
  file: File,
  opts: {
    maxFrames: number;
    maxDimension: number;
    signal?: AbortSignal;
    cropController?: CropController;
  },
  onFrame: (frame: VideoFrameInfo, index: number, total: number) => Promise<void>,
): Promise<void> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';

  try {
    video.src = url;
    await waitForLoadedMetadata(video);
    const duration = await resolveDuration(video);
    // resolveDuration がワークアラウンドで末尾へ一度シークしている可能性があるため、
    // ループに入る直前に改めてフレームが描画可能な状態になるまで待つ。
    await waitUntilFrameReady(video);

    // サンプリング点数: 15fps 相当を上限に、動画の長さに応じて間引く。短い動画では
    // maxFrames まで使わず、必要な本数だけサンプリングする。
    const sampleCount = Math.min(opts.maxFrames, Math.max(1, Math.round(duration * SAMPLING_FPS)));

    // 0〜duration の範囲で両端を含む等間隔のサンプル時刻を作る（単調増加が必須）。
    // 1点しか取れない場合は先頭を使う。
    const timestamps: number[] = [];
    for (let i = 0; i < sampleCount; i++) {
      const t = sampleCount === 1 ? 0 : (duration * i) / (sampleCount - 1);
      timestamps.push(t);
    }

    // クロップ無し（動画全体）のときの寸法。長辺を maxDimension まで縮小し、拡大はしない。
    const fullScale = Math.min(1, opts.maxDimension / Math.max(video.videoWidth, video.videoHeight));
    const fullWidth = Math.max(1, Math.round(video.videoWidth * fullScale));
    const fullHeight = Math.max(1, Math.round(video.videoHeight * fullScale));

    // canvas は1枚だけ確保して使い回す（毎フレーム作り直すとGCコストがかさむため）。
    // そのため onFrame コールバック内で canvas を非同期に持ち越して参照し続けてはいけない
    // （await を挟んで戻ってくる頃には次のフレームの内容に上書きされている）。
    // クロップ追従時は毎フレーム寸法が変わりうるため、実寸法は canvasWidth/canvasHeight で
    // 追跡し、変化したときだけ canvas.width/height を張り替える。
    let canvasWidth = fullWidth;
    let canvasHeight = fullHeight;
    let canvas: OffscreenCanvas | HTMLCanvasElement;
    if (typeof OffscreenCanvas !== 'undefined') {
      canvas = new OffscreenCanvas(canvasWidth, canvasHeight);
    } else {
      const htmlCanvas = document.createElement('canvas');
      htmlCanvas.width = canvasWidth;
      htmlCanvas.height = canvasHeight;
      canvas = htmlCanvas;
    }
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    if (!ctx) throw new Error('2D コンテキストの取得に失敗しました');

    for (let i = 0; i < timestamps.length; i++) {
      if (opts.signal?.aborted) {
        throw new DOMException('aborted', 'AbortError');
      }
      const ok = await seekTo(video, timestamps[i]);
      if (!ok) continue;

      // 呼び出し側が前フレームの検出結果から書き換えた rect を毎フレーム読み取る。
      const rect = opts.cropController?.rect ?? null;
      let sx: number;
      let sy: number;
      let sw: number;
      let sh: number;
      let targetWidth: number;
      let targetHeight: number;
      if (rect) {
        // 必ず元動画の実解像度からクロップする（縮小後の canvas から再クロップしない）。
        sx = rect.x * video.videoWidth;
        sy = rect.y * video.videoHeight;
        sw = Math.max(1, rect.w * video.videoWidth);
        sh = Math.max(1, rect.h * video.videoHeight);
        // クロップ時は縮小だけでなく拡大も許す（実解像度を稼ぐのが目的）。
        const cropScale = opts.maxDimension / Math.max(sw, sh);
        targetWidth = Math.max(1, Math.round(sw * cropScale));
        targetHeight = Math.max(1, Math.round(sh * cropScale));
      } else {
        sx = 0;
        sy = 0;
        sw = video.videoWidth;
        sh = video.videoHeight;
        targetWidth = fullWidth;
        targetHeight = fullHeight;
      }

      if (targetWidth !== canvasWidth || targetHeight !== canvasHeight) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        canvasWidth = targetWidth;
        canvasHeight = targetHeight;
      }
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvasWidth, canvasHeight);
      await onFrame(
        { canvas, width: canvasWidth, height: canvasHeight, timestampMs: timestamps[i] * 1000, cropRect: rect },
        i,
        timestamps.length,
      );
    }
  } finally {
    video.src = '';
    video.load();
    URL.revokeObjectURL(url);
  }
}
